import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import merge from 'lodash/merge';
import { ProfileContext } from 'context';
import { getStandards } from 'helpers/standards';
import MetricConversions from 'libs/MetricConversions';
import App from 'modules/App';
import Account from 'modules/Account';
import * as constants from '../constants';
const getKpiThresholds = (type, phiSet, clinicSettings)=>{
    let kpiThresholds = {};
    if (phiSet && phiSet.kpi && phiSet.kpi[type]) {
        kpiThresholds = phiSet.kpi[type];
    }
    const diabetesType = get(phiSet, 'diabetesType');
    if (clinicSettings && clinicSettings.kpi && clinicSettings.kpi[type]) {
        if (clinicSettings.kpi[type][diabetesType]) {
            kpiThresholds = merge(cloneDeep(clinicSettings.kpi[type][diabetesType]), kpiThresholds);
        } else {
            kpiThresholds = merge(cloneDeep(clinicSettings.kpi[type]), kpiThresholds);
        }
    }
    return kpiThresholds;
};
const getKpiLevel = ({ type, value, standards, readingsAverage, kpiThresholds, conversion })=>{
    const { allowedMeasurements = 0 } = kpiThresholds;
    let { valueHigh, valueLow } = kpiThresholds;
    switch(type){
        case constants.KPI_TYPES.GMI:
            {
                if (valueHigh === undefined) valueHigh = constants.GMI_THRESHOLDS.HIGH;
                if (valueLow === undefined) valueLow = constants.GMI_THRESHOLDS.LOW;
                if (value > valueHigh) return constants.KPI_LEVELS.HIGH;
                if (value < valueLow) return constants.KPI_LEVELS.LOW;
                return constants.KPI_LEVELS.TARGET;
            }
        case constants.KPI_TYPES.CV:
            {
                // TODO: Why NEUTRAL is default to valueHigh???
                if (valueHigh === undefined) valueHigh = constants.CV_THRESHOLDS.NEUTRAL;
                if (value > valueHigh) return constants.KPI_LEVELS.HIGH;
                return constants.KPI_LEVELS.NEUTRAL;
            }
        case constants.KPI_TYPES.DAYS_WITHOUT_READING:
            {
                if (value === 0) return constants.KPI_LEVELS.TARGET;
                return constants.KPI_LEVELS.HIGH;
            }
        case constants.KPI_TYPES.STANDARD_DEVIATION:
            {
                if (valueHigh) {
                    valueHigh = conversion.toDisplay(valueHigh);
                } else {
                    valueHigh = readingsAverage / 3;
                }
                if (value > valueHigh) return constants.KPI_LEVELS.HIGH;
                return constants.KPI_LEVELS.NEUTRAL;
            }
        case constants.KPI_TYPES.VERY_HIGH:
            {
                if (value <= allowedMeasurements) return constants.KPI_LEVELS.TARGET;
                return constants.KPI_LEVELS.HIGH;
            }
        case constants.KPI_TYPES.VERY_LOW:
            {
                if (value <= allowedMeasurements) return constants.KPI_LEVELS.TARGET;
                return constants.KPI_LEVELS.LOW;
            }
        case constants.KPI_TYPES.AVERAGE:
        case constants.KPI_TYPES.AVERAGE_POST_MEAL:
            {
                if (standards) {
                    const normalizedValue = conversion.toStorage(value, 2);
                    if (normalizedValue < standards.postMeal.lowThreshold) return constants.KPI_LEVELS.LOW;
                    if (normalizedValue < standards.postMeal.highThreshold) return constants.KPI_LEVELS.TARGET;
                }
                return constants.KPI_LEVELS.HIGH;
            }
        case constants.KPI_TYPES.AVERAGE_PRE_MEAL:
            {
                if (standards) {
                    const normalizedValue = conversion.toStorage(value, 2);
                    if (normalizedValue < standards.preMeal.lowThreshold) return constants.KPI_LEVELS.LOW;
                    if (normalizedValue < standards.preMeal.highThreshold) return constants.KPI_LEVELS.TARGET;
                }
                return constants.KPI_LEVELS.HIGH;
            }
        default:
            {
                return constants.KPI_LEVELS.NEUTRAL;
            }
    }
};
export const useKpiLevel = ({ type, value, readingsAverage = 0 })=>{
    const profileContext = useContext(ProfileContext);
    const phiSet = useSelector(profileContext.selectors.phiSet);
    const clinicSettings = useSelector(profileContext.selectors.clinicSettings);
    const countrySettings = useSelector(App.selectors.countrySettings);
    const metricsUnits = useSelector(Account.selectors.metricsUnits);
    const [level, setLevel] = useState(constants.KPI_LEVELS.NEUTRAL);
    useEffect(()=>{
        if (value === null) return;
        const kpiThresholds = getKpiThresholds(type, phiSet, clinicSettings);
        const metricConversions = new MetricConversions(metricsUnits);
        const conversion = metricConversions.bloodGlucoseConcentration;
        const standards = getStandards(phiSet, countrySettings, clinicSettings);
        const kpiLevel = getKpiLevel({
            type,
            value,
            readingsAverage,
            standards,
            kpiThresholds,
            conversion
        });
        setLevel(kpiLevel);
    }, [
        type,
        value,
        readingsAverage,
        phiSet
    ]);
    return level;
};
